"use client";

import { useEffect, useState } from "react";
import {
  Bot,
  Clock,
  Cpu,
  Download,
  Globe,
  ListChecks,
  Monitor,
  Search,
  Smartphone,
  Tablet,
  type LucideIcon,
} from "@suite/components/icons";
import {
  browserAccuracyNote,
  checkIsBrave,
  collectClientHintDetails,
  collectUaSignals,
  DEVICE_TYPE_LABEL,
  osAccuracyNote,
  resolveDisplayBrowser,
  resolveDisplayOs,
  type DeviceType,
  type ResolvedBrowser,
  type ResolvedOs,
  type UaReport,
  type UaSignal,
} from "@suite/lib/user-agent";
import { Card } from "@suite/components/ui/card";
import InfoTooltip from "@suite/components/ui/info-tooltip";
import { cn } from "@suite/lib/utils";
import CopyButton from "./CopyButton";

const HEADER = "font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70";

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "Browser & platform": Globe,
  "Client Hints": ListChecks,
  "Language & time": Clock,
  "Screen & display": Monitor,
  "Hardware & network": Cpu,
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Browser & platform": "What your browser identifies itself as",
  "Client Hints": "Structured browser data, Chromium only",
  "Language & time": "Your locale and clock",
  "Screen & display": "Your display as pages see it",
  "Hardware & network": "What the browser reports about this machine",
};

const DEVICE_ICONS: Record<DeviceType, LucideIcon> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  bot: Bot,
};

/** Short explanations for the signals most likely to confuse someone reading this page — reduced
 * or legacy values, and terms that mean something more specific than they sound like. Keyed by
 * signal label; a row without an entry here just shows its raw value, unexplained. */
const SIGNAL_TOOLTIPS: Record<string, string> = {
  "Browser (User-Agent string)":
    "What the raw UA string says. Some browsers, Brave chiefly, deliberately mirror another browser's UA string for compatibility — see the note above if that applies here.",
  "Operating system (User-Agent string)":
    "What the raw UA string says, which Chromium deliberately reduces on some platforms (see the note above if that applies here). Compare against the Client Hints platform rows below.",
  "Navigator platform":
    'A legacy identifier, frozen for backward compatibility — on Apple Silicon Macs it still reports "MacIntel", and it generally can\'t be relied on to reflect the actual CPU architecture.',
  "Client Hints platform":
    "The OS name from Client Hints, a structured replacement for the platform info in the UA string.",
  Brands:
    'The browser identities Client Hints reports. Includes deliberate "GREASE" noise brands (e.g. "Not)A;Brand") so sites can\'t over-fit to an exact browser list.',
  "Effective connection (browser estimate)":
    'The browser\'s own bucketed estimate of connection quality, from measured round-trip time and downlink — not a real speed test. It can read "4g" on a fast Wi-Fi or wired connection just as often as on an actual cellular link.',
  "Do Not Track": "A deprecated signal most sites and ad networks no longer honour.",
};

/** Group signals by category, preserving the order they were collected in (a plain object
 * would risk key reordering under numeric-looking keys; a Map never does). */
function groupByCategory(signals: UaSignal[]): [string, UaSignal[]][] {
  const byCategory = new Map<string, UaSignal[]>();
  for (const s of signals) {
    const items = byCategory.get(s.category);
    if (items) items.push(s);
    else byCategory.set(s.category, [s]);
  }
  return [...byCategory.entries()];
}

/** Everything below "Summary" already appears once, itemised, in the "Browser & platform"
 * category group — this used to repeat Browser/Engine/OS/Device/UA as a second header before that
 * same list, which is what QA flagged as Copy All duplicating the on-screen summary. */
function reportToText(
  report: UaReport,
  resolvedOs: ResolvedOs,
  resolvedBrowser: ResolvedBrowser,
  notes: string[]
): string {
  const lines = [
    `Summary: ${resolvedBrowser.name} ${resolvedBrowser.version} on ${resolvedOs.name} ${resolvedOs.version}`.trim(),
    ...notes.map((note) => `Note: ${note}`),
    "",
  ];
  for (const [category, items] of groupByCategory(report.signals)) {
    lines.push(`${category}:`);
    for (const s of items) lines.push(`  ${s.label}: ${s.value}`);
    lines.push("");
  }
  return lines.join("\n").trim();
}

interface SignalRowProps {
  signal: UaSignal;
}

function SignalRow({ signal }: SignalRowProps) {
  const tooltip = SIGNAL_TOOLTIPS[signal.label];
  return (
    <div className="-mx-1.5 flex items-start justify-between gap-3 rounded border-b border-white/[0.04] px-1.5 py-2 transition-colors last:border-0 hover:bg-white/[0.025]">
      <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold tracking-widest text-white/70 uppercase">
        {signal.label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </span>
      <span className="min-w-0 text-end font-mono text-[13px] [overflow-wrap:anywhere] break-words text-white/85">
        {signal.value}
      </span>
    </div>
  );
}

interface CategoryCardProps {
  category: string;
  items: UaSignal[];
}

function CategoryCard({ category, items }: CategoryCardProps) {
  const Icon = CATEGORY_ICONS[category] ?? Globe;
  const description = CATEGORY_DESCRIPTIONS[category];
  return (
    <div className="mb-3 break-inside-avoid rounded-lg bg-white/[0.02] ring-1 ring-white/[0.07]">
      <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-3.5 py-2.5">
        <span className="bg-brand-green/10 text-brand-green flex size-6 shrink-0 items-center justify-center rounded-md">
          <Icon className="size-3.5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[10px] font-semibold tracking-widest text-white/80 uppercase">
            {category}
          </span>
          {description && (
            <span className="mt-0.5 block truncate text-[11px] text-white/55">{description}</span>
          )}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-white/55">{items.length}</span>
      </div>
      <div className="px-3.5 pb-1">
        {items.map((s) => (
          <SignalRow key={s.label} signal={s} />
        ))}
      </div>
    </div>
  );
}

export default function UserAgentChecker() {
  const [report, setReport] = useState<UaReport | null>(null);
  const [isBrave, setIsBrave] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    // Strict Mode mounts, cleans up and remounts this effect once in development. Without this
    // guard both invocations' promises resolve and each appends its own copy of the extra
    // Client Hints signals onto the (already-merged) state, duplicating every high-entropy row.
    let cancelled = false;
    const initial = collectUaSignals();
    setReport(initial);
    // High-entropy Client Hints are gated behind a promise by the browser and arrive a tick
    // later; merge them into the Client Hints category once (if ever) they resolve.
    void collectClientHintDetails().then((extra) => {
      if (cancelled || extra.length === 0) return;
      setReport((current) =>
        current ? { ...current, signals: [...current.signals, ...extra] } : current
      );
    });
    // navigator.brave.isBrave() is Brave's own detection method — independent of Client Hints,
    // so it still catches Brave on a request that (for whatever reason) didn't expose the brand.
    void checkIsBrave().then((confirmed) => {
      if (!cancelled && confirmed) setIsBrave(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const shell = (announcement: string, children: React.ReactNode) => (
    <div className="ph-no-capture flex flex-col gap-5">
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {children}
    </div>
  );

  if (!report) {
    return shell(
      "Reading your browser's user agent.",
      <Card className="@container relative gap-0 overflow-hidden p-0">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
        </div>
        <div className="relative flex flex-col gap-2 p-5 @4xl:py-7">
          <div className="skeleton-bar h-7 w-72 max-w-full rounded-full" />
          <div className="skeleton-bar mt-2 h-4 w-full max-w-xl rounded-full" />
        </div>
      </Card>
    );
  }

  const { parsed } = report;
  const DeviceIcon = DEVICE_ICONS[parsed.deviceType];
  const groups = groupByCategory(report.signals);

  // Prefer Client Hints for the headline OS when it can resolve one: the raw UA string is
  // deliberately reduced on both platforms this matters for (macOS frozen to 10.15.7, Windows
  // 11 indistinguishable from 10), while Client Hints isn't frozen the same way.
  const clientHintPlatform = report.signals.find(
    (s) => s.category === "Client Hints" && s.label === "Client Hints platform"
  )?.value;
  const clientHintPlatformVersion = report.signals.find(
    (s) => s.category === "Client Hints" && s.label === "Platform version"
  )?.value;
  const resolvedOs = resolveDisplayOs(parsed, clientHintPlatform, clientHintPlatformVersion);
  const osNote = osAccuracyNote(parsed, resolvedOs);
  const resolvedBrowser = resolveDisplayBrowser(parsed, report.brands, isBrave);
  const browserNote = browserAccuracyNote(parsed, resolvedBrowser);
  const notes = [browserNote, osNote].filter((n): n is string => n !== null);

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map(
          ([category, items]) =>
            [
              category,
              items.filter((s) => `${category} ${s.label} ${s.value}`.toLowerCase().includes(q)),
            ] as [string, UaSignal[]]
        )
        .filter(([, items]) => items.length > 0)
    : groups;
  const matchCount = filteredGroups.reduce((n, [, items]) => n + items.length, 0);

  const downloadJson = () => {
    // Carries the resolved browser/OS and their accuracy notes alongside the raw report, so the
    // download can never disagree with what the headline above actually claims.
    const presented = { ...report, resolvedOs, resolvedBrowser, notes };
    const blob = new Blob([JSON.stringify(presented, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "user-agent.json";
    a.hidden = true;
    document.body.append(a);
    a.click();
    a.remove();
    // Safari may not consume the object URL until after the click task has returned.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return shell(
    `${resolvedBrowser.name} ${resolvedBrowser.version} on ${resolvedOs.name} ${resolvedOs.version}. Detected as ${DEVICE_TYPE_LABEL[parsed.deviceType]}.${notes.length ? ` ${notes.join(" ")}` : ""}`,
    <div className="flex flex-col gap-5">
      <Card className="@container relative gap-0 overflow-hidden p-0">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
        </div>
        <div className="relative flex flex-col gap-3 p-5 @4xl:py-7">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="bg-brand-green/10 text-brand-green flex size-8 shrink-0 items-center justify-center rounded-md">
              <DeviceIcon className="size-4" aria-hidden />
            </span>
            <h2 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
              {resolvedBrowser.name} {resolvedBrowser.version} on {resolvedOs.name}{" "}
              {resolvedOs.version}
            </h2>
            {resolvedBrowser.source === "client-hints" && (
              <span
                className="rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-[9px] font-semibold tracking-widest text-white/60 uppercase"
                title="Chrome's user agent string is Brave's compatibility mask; identified via Client Hints / navigator.brave.isBrave()"
              >
                Brave detected
              </span>
            )}
            {resolvedOs.source === "client-hints" && (
              <span
                className="rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-[9px] font-semibold tracking-widest text-white/60 uppercase"
                title="Resolved from Client Hints, which isn't reduced the way the raw user agent string is"
              >
                OS via Client Hints
              </span>
            )}
          </div>
          <p className="text-sm leading-relaxed text-white/65">
            Detected as{" "}
            <span className="text-white/85">{DEVICE_TYPE_LABEL[parsed.deviceType]}</span>, running
            the {parsed.engineName} engine
            {parsed.engineVersion ? ` (${parsed.engineVersion})` : ""}.
            {parsed.inAppBrowser && ` Opened inside the ${parsed.inAppBrowser} in-app browser.`}
          </p>
          {notes.map((note) => (
            <p key={note} className="text-sm leading-relaxed text-amber-300/85">
              {note}
            </p>
          ))}
          <div className="mt-1 flex items-start justify-between gap-2 rounded-md bg-black/25 px-3 py-2.5 ring-1 ring-white/[0.06]">
            <code className="min-w-0 font-mono text-[12px] leading-relaxed [overflow-wrap:anywhere] break-words text-white/80">
              {report.raw}
            </code>
            <CopyButton value={report.raw} label="user agent string" className="shrink-0" />
          </div>
        </div>
      </Card>

      <Card
        id="signal-inspector"
        tabIndex={-1}
        className="focus-visible:ring-brand-green/70 scroll-mt-24 px-5 outline-none focus-visible:ring-2"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>Signal inspector ({report.signals.length})</span>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <Search
                className="pointer-events-none absolute start-2 top-1/2 size-3 -translate-y-1/2 text-white/55"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter signals…"
                aria-label="Filter signals"
                className="focus:ring-brand-green/40 w-36 rounded-md bg-white/[0.04] py-1 ps-7 pe-2 text-xs text-white/85 ring-1 ring-white/[0.08] outline-none placeholder:text-white/55 focus:ring-1 sm:w-48"
              />
            </div>
            <CopyButton
              value={reportToText(report, resolvedOs, resolvedBrowser, notes)}
              label="full report"
            />
            <button
              type="button"
              onClick={downloadJson}
              aria-label="Download as JSON"
              className="focus-visible:ring-brand-green/70 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
            >
              <Download className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
        {q && (
          <p className="text-[11px] text-white/60">
            {matchCount} of {report.signals.length} match
          </p>
        )}
        {filteredGroups.length > 0 ? (
          <div className={cn("gap-3 md:columns-2")}>
            {filteredGroups.map(([category, items]) => (
              <CategoryCard key={category} category={category} items={items} />
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-white/55">
            No signals match &ldquo;{query}&rdquo;
          </p>
        )}
      </Card>
    </div>
  );
}
